// JWT Token utilities

/**
 * Decode JWT token to extract user profile
 * @param {string} token - JWT token string
 * @returns {object|null} UserProfile object or null if invalid
 */
export function decodeJWTToken(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }
    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload);
    const userProfile = JSON.parse(decodedPayload);
    if (!userProfile.id || !userProfile.email || !userProfile.name) {
      console.error('Missing required fields in token payload');
      return null;
    }
    return userProfile;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} true if expired
 */
export function isTokenExpired(token) {
  const profile = decodeJWTToken(token);
  if (!profile) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime > profile.exp;
}

/**
 * Get user profile from stored token
 * @returns {object|null} UserProfile object or null
 */
export function getCurrentUserProfile() {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  if (isTokenExpired(token)) {
    localStorage.removeItem('authToken');
    return null;
  }
  return decodeJWTToken(token);
}

/**
 * Format the profile for display
 * @param {object} profile - UserProfile object
 */
export function formatUserProfile(profile) {
  return {
    id: profile.id,
    email: profile.email,
    name: profile.name,
    profilePicture: profile.profilePicture || '',
    initials: profile.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2),
    issuedAt: new Date(profile.iat * 1000).toLocaleString(),
    expiresAt: new Date(profile.exp * 1000).toLocaleString(),
    timeUntilExpiry: Math.max(0, profile.exp - Math.floor(Date.now() / 1000)),
  };
}

/**
 * Get userId from stored JWT token
 * @returns {string|null}
 */
export function getUserIdFromToken() {
  // The app stores the JWT under "authToken" (set in login.tsx / callback.tsx)
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userId || payload._id || payload.id || payload.sub;
  } catch {
    return null;
  }
}

/**
 * Get the raw auth token string from localStorage.
 * Used for SSE connections (EventSource can't set Authorization headers)
 * so the token is passed as ?token= query param instead.
 * @returns {string|null}
 */
export function getAuthToken() {
  return localStorage.getItem('authToken');
}

